import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["className", "color", "name", "screenReaderText", "style", "size"];
/* hs-eslint ignored failing-rules */
/* eslint-disable react-hooks/exhaustive-deps */

import classNames from 'classnames';
// @ts-expect-error iconAliases implicitly has any type.
import iconAliases from 'icons/iconAliases';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState, memo } from 'react';
import devLogger from 'react-utils/devLogger';
import memoize from 'react-utils/memoize';
import styled from 'styled-components';
import { getIconNamePropType } from '../utils/propTypes/iconName';
import { ICON_SIZES } from './IconConstants';
import { getWindow } from '../types/Window';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const sizeToRes = size => size <= ICON_SIZES.small ? 'low' : 'high';
const getIconClassName = (size, measuredRes) => {
  const res = size != null ? sizeToRes(ICON_SIZES[size] || size) : measuredRes;
  return `private-icon__${res}`;
};
const getComputedFontSize = size => {
  if (!size) return undefined;
  if (ICON_SIZES[size]) {
    return ICON_SIZES[size];
  }
  return size;
};
const IconContent = styled.span.withConfig({
  displayName: "UIIcon__IconContent",
  componentId: "uux245-0"
})(["font:inherit !important;&::before{content:'", "';}"], ({
  ligature
}) => ligature);
const getIconContent = memoize(ligature => {
  // Ligature is force cast as a string in this case since memoize function parameters are either string | number
  return /*#__PURE__*/_jsx(IconContent, {
    ligature: ligature,
    "aria-hidden": true
  });
});
let hasPingedNewRelic = false;
const UIIconComponent = props => {
  const {
      className,
      color,
      name,
      screenReaderText,
      style,
      size
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded);
  const [measuredRes, setMeasuredRes] = useState('low');
  const measureRef = useRef(null);
  useEffect(() => {
    if (size) return;
    // Measure the `fontSize` in the DOM to determine whether this is high- or low-res.
    const styles = measureRef.current && getComputedStyle(measureRef.current, null);

    // Patch for Firefox < 62: https://bugzilla.mozilla.org/show_bug.cgi?id=1467722
    if (!styles) return;
    const newMeasuredRes = sizeToRes(parseInt(styles.fontSize, 10));
    if (newMeasuredRes !== measuredRes) setMeasuredRes(newMeasuredRes);
  });
  if (iconAliases[name]) {
    const window = getWindow();
    if (window.newrelic && !hasPingedNewRelic) {
      hasPingedNewRelic = true;
      window.newrelic.addPageAction('icon-alias', {
        alias: name
      });
    }
    if (process.env.NODE_ENV !== 'production') {
      devLogger.warn({
        message: `UIIcon: "${name}" is a deprecated alias. Please use the canonical icon name.`,
        key: `UIIcon-${name}`,
        url: 'https://tools.hubteamqa.com/ui-library/styles/icons'
      });
    }
  }
  const computedName = iconAliases[name] || name;
  const computedClassName = classNames('private-icon', getIconClassName(size, measuredRes), className);
  return /*#__PURE__*/_jsxs("span", Object.assign({}, rest, {
    className: computedClassName,
    "data-icon-name": computedName,
    ref: measureRef,
    style: Object.assign({
      color
    }, style, {
      fontSize: getComputedFontSize(size)
    }),
    children: [getIconContent(computedName), screenReaderText &&
    /*#__PURE__*/
    // Spaces around screenReaderText prevents collisions withsiblingtext.
    _jsxs("span", {
      className: "sr-only",
      children: [" ", screenReaderText, " "]
    })]
  }));
};
const UIIcon = /*#__PURE__*/memo(UIIconComponent);
const iconSizeKeys = Object.keys(ICON_SIZES);
const propTypes = {
  color: PropTypes.string,
  name: getIconNamePropType().isRequired,
  screenReaderText: PropTypes.string,
  size: PropTypes.oneOfType([PropTypes.oneOf(iconSizeKeys), PropTypes.number]),
  placement: PropTypes.string
};
UIIcon.propTypes = propTypes;
UIIcon.displayName = 'UIIcon';
export default UIIcon;